import React, { useState, useCallback, useEffect } from 'react';
import * as SVGO from 'svgo/dist/svgo.browser.js';

const SVGTools = () => {
  const [inputSvg, setInputSvg] = useState('');
  const [outputSvg, setOutputSvg] = useState('');
  const [cssBackground, setCssBackground] = useState('');
  const [originalSize, setOriginalSize] = useState(0);
  const [optimizedSize, setOptimizedSize] = useState(0);
  const [optimizationRunning, setOptimizationRunning] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [activeOutputTab, setActiveOutputTab] = useState('svg');

  // SVGO plugin configuration - similar to SVGOMG
  const [pluginConfig, setPluginConfig] = useState({
    // Preset default plugins
    cleanupAttrs: { enabled: true, description: 'cleanup attributes from newlines, trailing, and repeating spaces' },
    mergeStyles: { enabled: true, description: 'merge multiple style elements into one' },
    inlineStyles: { enabled: true, description: 'move and merge styles from <style> elements to element style attributes' },
    removeDoctype: { enabled: true, description: 'remove doctype declaration' },
    removeXMLProcInst: { enabled: true, description: 'remove XML processing instructions' },
    removeComments: { enabled: true, description: 'remove comments' },
    removeMetadata: { enabled: true, description: 'remove <metadata>' },
    removeTitle: { enabled: true, description: 'remove <title>' },
    removeDesc: { enabled: true, description: 'remove <desc>' },
    removeUselessDefs: { enabled: true, description: 'remove elements of <defs> without id' },
    removeXMLNS: { enabled: false, description: 'removes the xmlns attribute (for inline SVG)' },
    removeEditorsNSData: { enabled: true, description: 'remove editors namespaces, elements, and attributes' },
    removeEmptyAttrs: { enabled: true, description: 'remove empty attributes' },
    removeHiddenElems: { enabled: true, description: 'remove hidden elements' },
    removeEmptyText: { enabled: true, description: 'remove empty Text elements' },
    removeEmptyContainers: { enabled: true, description: 'remove empty Container elements' },
    removeViewBox: { enabled: false, description: 'remove viewBox attribute when possible' },
    cleanupEnableBackground: { enabled: true, description: 'remove or cleanup enable-background attribute when possible' },
    convertStyleToAttrs: { enabled: true, description: 'convert styles into attributes' },
    convertColors: { enabled: true, description: 'convert colors (from rgb() to #rrggbb, from #rrggbb to #rgb)' },
    convertPathData: { enabled: true, description: 'convert Path data to relative or absolute (whichever is shorter), convert one segment to another, trim useless delimiters, smart rounding, and much more' },
    convertTransform: { enabled: true, description: 'collapse multiple transforms into one, convert matrices to the short aliases, and much more' },
    removeUnknownsAndDefaults: { enabled: true, description: 'remove unknown elements content and attributes, remove attrs with default values' },
    removeNonInheritableGroupAttrs: { enabled: true, description: "remove non-inheritable group's presentation attributes" },
    removeUselessStrokeAndFill: { enabled: true, description: 'remove useless stroke and fill attrs' },
    removeUnusedNS: { enabled: true, description: 'remove unused namespaces declaration' },
    cleanupIds: { enabled: true, description: 'remove unused and minify used IDs' },
    cleanupNumericValues: { enabled: true, description: 'round numeric values to the fixed precision, remove default px units' },
    cleanupListOfValues: { enabled: false, description: 'round numeric values in attributes that take a list of numbers (like viewBox or enable-background)' },
    moveElemsAttrsToGroup: { enabled: true, description: "move elements' attributes to their enclosing group" },
    moveGroupAttrsToElems: { enabled: true, description: 'move some group attributes to the contained elements' },
    collapseGroups: { enabled: true, description: 'collapse useless groups' },
    removeRasterImages: { enabled: false, description: 'remove raster images' },
    mergePaths: { enabled: true, description: 'merge multiple Paths into one' },
    convertShapeToPath: { enabled: true, description: 'convert some basic shapes to <path>' },
    sortAttrs: { enabled: false, description: 'sort element attributes for epic readability' },
    removeDimensions: { enabled: true, description: "remove width/height and add viewBox if it's missing (opposite to removeViewBox, disable it first)" },
    removeAttrs: { enabled: false, description: 'remove attributes by pattern' },
    removeAttributesBySelector: { enabled: false, description: 'removes attributes of elements that match a CSS selector' },
    removeElementsByAttr: { enabled: false, description: 'remove arbitrary elements by ID or className' },
    addClassesToSVGElement: { enabled: false, description: 'add classnames to an outer <svg> element' },
    addAttributesToSVGElement: { enabled: false, description: 'adds attributes to an outer <svg> element' },
    removeOffCanvasPaths: { enabled: false, description: 'removes elements that are drawn outside of the viewbox' },
    reusePaths: { enabled: false, description: 'Find duplicated <path> elements and replace them with <use> elements' }
  });

  // URL encode SVG for CSS background use (based on yoksel/url-encoder)
  const encodeSvgForCss = useCallback((svgString) => {
    if (!svgString) return '';
    
    // Encode special characters for CSS background-image usage
    const encoded = svgString
      .replace(/"/g, "'")
      .replace(/%/g, "%25")
      .replace(/#/g, "%23")
      .replace(/{/g, "%7B")
      .replace(/}/g, "%7D")
      .replace(/</g, "%3C")
      .replace(/>/g, "%3E")
      .replace(/\s+/g, " ")
      .trim();
    
    return `background-image: url("data:image/svg+xml,${encoded}");`;
  }, []);

  // Build SVGO config from plugin states
  const buildSvgoConfig = useCallback(() => {
    const plugins = [
      {
        name: 'preset-default',
        params: {
          overrides: {
            // Configure individual plugins based on our settings
            removeViewBox: pluginConfig.removeViewBox?.enabled ?? false,
            removeDimensions: pluginConfig.removeDimensions?.enabled ?? true,
            removeTitle: pluginConfig.removeTitle?.enabled ?? true,
            removeDesc: pluginConfig.removeDesc?.enabled ?? true,
            removeMetadata: pluginConfig.removeMetadata?.enabled ?? true,
            removeComments: pluginConfig.removeComments?.enabled ?? true,
            removeEditorsNSData: pluginConfig.removeEditorsNSData?.enabled ?? true,
            removeEmptyAttrs: pluginConfig.removeEmptyAttrs?.enabled ?? true,
            removeEmptyText: pluginConfig.removeEmptyText?.enabled ?? true,
            removeEmptyContainers: pluginConfig.removeEmptyContainers?.enabled ?? true,
            removeUnusedNS: pluginConfig.removeUnusedNS?.enabled ?? true,
            cleanupAttrs: pluginConfig.cleanupAttrs?.enabled ?? true,
            cleanupNumericValues: pluginConfig.cleanupNumericValues?.enabled ?? true,
            convertColors: pluginConfig.convertColors?.enabled ?? true,
            removeHiddenElems: pluginConfig.removeHiddenElems?.enabled ?? true,
            removeUnknownsAndDefaults: pluginConfig.removeUnknownsAndDefaults?.enabled ?? true,
            removeNonInheritableGroupAttrs: pluginConfig.removeNonInheritableGroupAttrs?.enabled ?? true,
            removeUselessStrokeAndFill: pluginConfig.removeUselessStrokeAndFill?.enabled ?? true,
            cleanupIds: pluginConfig.cleanupIds?.enabled ?? true,
            moveElemsAttrsToGroup: pluginConfig.moveElemsAttrsToGroup?.enabled ?? true,
            moveGroupAttrsToElems: pluginConfig.moveGroupAttrsToElems?.enabled ?? true,
            collapseGroups: pluginConfig.collapseGroups?.enabled ?? true,
            mergePaths: pluginConfig.mergePaths?.enabled ?? true,
            convertShapeToPath: pluginConfig.convertShapeToPath?.enabled ?? true,
            sortAttrs: pluginConfig.sortAttrs?.enabled ?? false,
            convertPathData: pluginConfig.convertPathData?.enabled ?? true,
            convertTransform: pluginConfig.convertTransform?.enabled ?? true,
            removeRasterImages: pluginConfig.removeRasterImages?.enabled ?? false
          }
        }
      }
    ];

    return {
      plugins: plugins,
      js2svg: {
        indent: 2,
        pretty: true
      }
    };
  }, [pluginConfig]);

  // Optimize SVG using SVGO
  const optimizeSvg = useCallback(async (svgContent) => {
    if (!svgContent.trim()) {
      setOutputSvg('');
      setCssBackground('');
      setOptimizedSize(0);
      return;
    }

    setOptimizationRunning(true);
    setError('');

    try {
      const result = SVGO.optimize(svgContent, buildSvgoConfig());
      
      if (result.error) {
        setError(result.error);
        setOutputSvg('');
        setCssBackground('');
        setOptimizedSize(0);
      } else {
        setOutputSvg(result.data);
        setCssBackground(encodeSvgForCss(result.data));
        setOptimizedSize(new Blob([result.data]).size);
      }
    } catch (err) {
      setError(err.message || 'Optimization failed');
      setOutputSvg('');
      setCssBackground('');
      setOptimizedSize(0);
    } finally {
      setOptimizationRunning(false);
    }
  }, [buildSvgoConfig, encodeSvgForCss]);

  // Auto-update optimization when plugin config changes
  useEffect(() => {
    if (inputSvg.trim()) {
      const timer = setTimeout(() => {
        optimizeSvg(inputSvg);
      }, 300); // Short delay to debounce rapid changes
      
      return () => clearTimeout(timer);
    }
  }, [pluginConfig, inputSvg, optimizeSvg]);

  // Handle input changes with debouncing
  const handleInputChange = useCallback((value) => {
    setInputSvg(value);
    setOriginalSize(new Blob([value]).size);
    
    // Debounce optimization
    const timer = setTimeout(() => {
      optimizeSvg(value);
    }, 500);

    return () => clearTimeout(timer);
  }, [optimizeSvg]);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    const svgFile = files.find(file => file.type === 'image/svg+xml' || file.name.endsWith('.svg'));
    
    if (svgFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        handleInputChange(event.target.result);
      };
      reader.readAsText(svgFile);
    }
  }, [handleInputChange]);

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // Toggle plugin enabled state
  const togglePlugin = useCallback((pluginName) => {
    setPluginConfig(prev => ({
      ...prev,
      [pluginName]: {
        ...prev[pluginName],
        enabled: !prev[pluginName].enabled
      }
    }));
  }, []);

  // Preset configurations
  const applyPreset = useCallback((presetName) => {
    let newConfig = { ...pluginConfig };
    
    switch (presetName) {
      case 'aggressive':
        Object.keys(newConfig).forEach(key => {
          newConfig[key] = { ...newConfig[key], enabled: true };
        });
        break;
      case 'conservative':
        Object.keys(newConfig).forEach(key => {
          newConfig[key] = { ...newConfig[key], enabled: false };
        });
        // Enable only safe plugins
        ['cleanupAttrs', 'removeComments', 'removeMetadata', 'removeTitle', 'removeDesc', 'removeEmptyAttrs', 'removeEmptyText'].forEach(plugin => {
          if (newConfig[plugin]) {
            newConfig[plugin].enabled = true;
          }
        });
        break;
      case 'default':
        // Reset to default configuration
        Object.keys(newConfig).forEach(key => {
          // Most plugins enabled by default except risky ones
          newConfig[key] = { 
            ...newConfig[key], 
            enabled: !['removeViewBox', 'removeXMLNS', 'removeDimensions', 'removeRasterImages', 'sortAttrs', 'removeAttrs', 'removeAttributesBySelector', 'removeElementsByAttr', 'addClassesToSVGElement', 'addAttributesToSVGElement', 'removeOffCanvasPaths', 'reusePaths', 'cleanupListOfValues'].includes(key)
          };
        });
        break;
      default:
        // No action needed
        break;
    }
    
    setPluginConfig(newConfig);
  }, [pluginConfig]);

  // Calculate savings
  const savings = originalSize > 0 ? Math.round(((originalSize - optimizedSize) / originalSize) * 100) : 0;

  // Format file size
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Download SVG
  const downloadSvg = (content, filename) => {
    const blob = new Blob([content], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <h1 className="text-3xl font-bold text-white mb-4">SVG Optimizer</h1>
          <p className="text-gray-300">
            Optimize your SVG files with SVGO. Upload or paste your SVG content to get started.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sticky top-4">
              <h3 className="text-lg font-semibold text-white mb-4">Optimization Settings</h3>
              
              {/* Stats */}
              {originalSize > 0 && (
                <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                  <div className="text-sm text-gray-300 mb-2">
                    <div className="flex justify-between">
                      <span>Original:</span>
                      <span className="text-white">{formatBytes(originalSize)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Optimized:</span>
                      <span className="text-white">{formatBytes(optimizedSize)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-green-400">
                      <span>Saved:</span>
                      <span>{savings}%</span>
                    </div>
                  </div>
                  {savings > 0 && (
                    <div className="w-full bg-gray-600 rounded-full h-2 mt-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${savings}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Presets */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Quick Presets</label>
                <div className="space-y-1">
                  <button
                    onClick={() => applyPreset('conservative')}
                    className="w-full text-left px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                  >
                    Conservative
                  </button>
                  <button
                    onClick={() => applyPreset('default')}
                    className="w-full text-left px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                  >
                    Default
                  </button>
                  <button
                    onClick={() => applyPreset('aggressive')}
                    className="w-full text-left px-3 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                  >
                    Aggressive
                  </button>
                </div>
              </div>

              {/* Plugin Controls */}
              <div className="space-y-1 max-h-96 overflow-y-auto">
                <label className="block text-sm font-medium text-gray-300 mb-2">Plugins</label>
                {Object.entries(pluginConfig).map(([pluginName, config]) => (
                  <div key={pluginName} className="group">
                    <label className="flex items-start space-x-2 text-sm p-2 hover:bg-gray-700 rounded transition-colors">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={() => togglePlugin(pluginName)}
                        className="mt-0.5 rounded border-gray-500 bg-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-2"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-300 text-xs font-medium">
                          {pluginName}
                        </span>
                        <p className="text-gray-400 text-xs mt-1 leading-tight">
                          {config.description}
                        </p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Input Panel */}
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              <div className="p-4 border-b border-gray-700">
                <h3 className="text-lg font-semibold text-white">Input SVG</h3>
              </div>
              <div className="p-4">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                    dragActive
                      ? 'border-blue-400 bg-blue-400/10'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <textarea
                    value={inputSvg}
                    onChange={(e) => handleInputChange(e.target.value)}
                    placeholder="Paste your SVG code here or drop an SVG file..."
                    className="w-full h-64 bg-gray-700 text-white rounded border border-gray-600 p-3 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-gray-400 text-sm">
                      Drop SVG files here or paste code above
                    </p>
                    {inputSvg && (
                      <button
                        onClick={() => copyToClipboard(inputSvg)}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        Copy
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Output Panel */}
            {(outputSvg || error || optimizationRunning) && (
              <div className="bg-gray-800 rounded-lg border border-gray-700">
                <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">Optimized Output</h3>
                  {outputSvg && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(outputSvg)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                      >
                        Copy SVG
                      </button>
                      <button
                        onClick={() => copyToClipboard(cssBackground)}
                        className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded transition-colors"
                      >
                        Copy CSS
                      </button>
                      <button
                        onClick={() => downloadSvg(outputSvg, 'optimized.svg')}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                      >
                        Download
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  {optimizationRunning && (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <p className="text-gray-400 mt-2">Optimizing...</p>
                    </div>
                  )}
                  
                  {error && (
                    <div className="bg-red-900/50 border border-red-500 rounded p-4 text-red-200">
                      <strong>Error:</strong> {error}
                    </div>
                  )}
                  
                  {outputSvg && !optimizationRunning && (
                    <div className="space-y-4">
                      {/* Output Tabs */}
                      <div className="flex space-x-1 bg-gray-700 rounded-lg p-1">
                        <button
                          onClick={() => setActiveOutputTab('svg')}
                          className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                            activeOutputTab === 'svg'
                              ? 'bg-gray-600 text-white'
                              : 'text-gray-300 hover:text-white'
                          }`}
                        >
                          SVG Code
                        </button>
                        <button
                          onClick={() => setActiveOutputTab('css')}
                          className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                            activeOutputTab === 'css'
                              ? 'bg-gray-600 text-white'
                              : 'text-gray-300 hover:text-white'
                          }`}
                        >
                          CSS Background
                        </button>
                        <button
                          onClick={() => setActiveOutputTab('preview')}
                          className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                            activeOutputTab === 'preview'
                              ? 'bg-gray-600 text-white'
                              : 'text-gray-300 hover:text-white'
                          }`}
                        >
                          Preview
                        </button>
                      </div>

                      {/* SVG Code Tab */}
                      {activeOutputTab === 'svg' && (
                        <textarea
                          value={outputSvg}
                          readOnly
                          className="w-full h-64 bg-gray-700 text-white rounded border border-gray-600 p-3 resize-none"
                        />
                      )}

                      {/* CSS Background Tab */}
                      {activeOutputTab === 'css' && (
                        <div className="space-y-3">
                          <textarea
                            value={cssBackground}
                            readOnly
                            className="w-full h-32 bg-gray-700 text-white rounded border border-gray-600 p-3 resize-none"
                          />
                          <div className="text-sm text-gray-400">
                            <p className="mb-2"><strong>Usage:</strong> Copy and paste this CSS rule to use the SVG as a background image.</p>
                            <p>You can also add additional properties like <code className="bg-gray-700 px-1 rounded">background-size: contain;</code> or <code className="bg-gray-700 px-1 rounded">background-repeat: no-repeat;</code></p>
                          </div>
                        </div>
                      )}

                      {/* Preview Tab */}
                      {activeOutputTab === 'preview' && (
                        <div className="border border-gray-600 rounded p-4 bg-white">
                          <div className="text-gray-800 text-sm mb-2">SVG Preview:</div>
                          <div 
                            className="flex justify-center"
                            dangerouslySetInnerHTML={{ __html: outputSvg }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Features Info */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-semibold text-white mb-4">About SVG Optimization</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-300">
            <div>
              <h4 className="font-semibold text-white mb-2">File Size Reduction</h4>
              <p>Remove unnecessary metadata, comments, and unused elements to significantly reduce file size.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Path Optimization</h4>
              <p>Optimize path data and convert shapes to more efficient representations.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Attribute Cleanup</h4>
              <p>Clean up attributes, merge duplicate styles, and remove default values.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Colour Optimization</h4>
              <p>Convert colours to shorter hex codes and remove unnecessary colour information.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Structure Simplification</h4>
              <p>Remove empty containers, merge paths, and simplify nested groups.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Professional Grade</h4>
              <p>Powered by SVGO - the industry standard SVG optimization library used by millions.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SVGTools; 